// WorldCup 2026 API — https://worldcup26.ir
// Public endpoints (no auth). CORS_ORIGINS=* → works from browser.
// Rate limit: 120 req/min per IP. Cache TTL serveur: 30s.
// On cache côté client 60s pour éviter les appels redondants.

const BASE = "https://worldcup26.ir/get";
const CLIENT_CACHE_TTL = 60_000; // 60 secondes

// ── Cache en mémoire ─────────────────────────────────────────────────────────
let cachedData: WC26Data | null = null;
let cacheTime = 0;

// ── Timezone UTC offset par stade (DST été 2026) ──────────────────────────────
// Toutes les phases de groupe + knockout se jouent avril-juillet → DST actif.
// Source : identifiants de stade de l'API worldcup26.ir /get/stadiums
const STADIUM_UTC_OFFSET: Record<string, number> = {
  "1":  -5,  // Estadio Azteca — Mexico City (CDT)
  "2":  -5,  // Estadio Akron — Guadalajara (CDT)
  "3":  -5,  // Estadio BBVA — Monterrey (CDT)
  "4":  -5,  // AT&T Stadium — Dallas (CDT)
  "5":  -5,  // NRG Stadium — Houston (CDT)
  "6":  -5,  // Arrowhead Stadium — Kansas City (CDT)
  "7":  -4,  // Mercedes-Benz Stadium — Atlanta (EDT)
  "8":  -4,  // Hard Rock Stadium — Miami (EDT)
  "9":  -4,  // Gillette Stadium — Boston (EDT)
  "10": -4,  // Lincoln Financial Field — Philadelphia (EDT)
  "11": -4,  // MetLife Stadium — New York (EDT)
  "12": -4,  // BMO Field — Toronto (EDT)
  "13": -7,  // BC Place — Vancouver (PDT)
  "14": -7,  // Lumen Field — Seattle (PDT)
  "15": -7,  // Levi's Stadium — San Francisco Bay Area (PDT)
  "16": -7,  // SoFi Stadium — Los Angeles (PDT)
};

// ── Types API bruts ───────────────────────────────────────────────────────────
interface ApiGame {
  id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: string;
  away_score: string;
  group: string;           // "A"–"L" | "R32" | "R16" | "QF" | "SF" | "3RD" | "FINAL"
  matchday: string;
  local_date: string;      // "MM/DD/YYYY HH:MM" — heure locale du stade
  stadium_id: string;
  finished: "TRUE" | "FALSE";
  time_elapsed: string;    // "finished" | "notstarted" | "45'" etc.
  type: string;            // "group" | "r32" | "r16" | "qf" | "sf" | "third" | "final"
  home_team_name_en?: string;
  away_team_name_en?: string;
  home_team_label?: string; // ex: "Runner-up Group A"
  away_team_label?: string;
}

// ── Types sortants ────────────────────────────────────────────────────────────
export interface WC26Match {
  id: string;
  grp: string;
  date: string;        // "YYYY-MM-DD" en KST — jour KST réel du coup d'envoi
  koUTC: string;       // ISO UTC exact du coup d'envoi
  t1: string;
  t2: string;
  p1: number;
  pX: number;
  p2: number;
  status: "scheduled" | "finished" | "live";
  label1?: string;     // label knockout quand équipe inconnue
  label2?: string;
}

export interface WC26Result {
  matchId: string;
  o: "team1" | "draw" | "team2";
  s: string;           // "2-0"
}

export interface WC26Data {
  matches: WC26Match[];
  results: WC26Result[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// "06/13/2026 21:00" + stadiumId → { koUTC: "2026-06-14T04:00:00.000Z", date: "2026-06-14" }
// Utilise le vrai UTC offset du stade (PDT/CDT/EDT) au lieu d'une approximation globale.
// date est le jour KST du coup d'envoi — c'est ce qui sert au groupement dans l'app.
function parseLocalDate(raw: string, stadiumId: string): { date: string; koUTC: string } {
  const [datePart, timePart] = raw.split(" ");
  const [mm, dd, yyyy] = datePart.split("/");
  const [hh, min] = (timePart ?? "00:00").split(":");

  const offsetH = STADIUM_UTC_OFFSET[stadiumId] ?? -5; // CDT par défaut si stade inconnu

  // Convertit l'heure locale en UTC en soustrayant l'offset négatif (= ajouter |offset|)
  const utcMs =
    Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), 0) -
    offsetH * 3_600_000;

  const koUTC = new Date(utcMs).toISOString();
  const date = new Date(utcMs).toLocaleDateString("en-CA", { timeZone: "Asia/Seoul" });

  return { date, koUTC };
}

function toOutcome(hs: string, as_: string): "team1" | "draw" | "team2" {
  const h = Number(hs);
  const a = Number(as_);
  if (h > a) return "team1";
  if (h < a) return "team2";
  return "draw";
}

// ── Fetch principal ───────────────────────────────────────────────────────────
export async function fetchWC26Data(): Promise<WC26Data | null> {
  // Retourne le cache si encore valide
  if (cachedData && Date.now() - cacheTime < CLIENT_CACHE_TTL) {
    return cachedData;
  }

  try {
    const res = await fetch(`${BASE}/games`);
    if (!res.ok) return null;

    const json = await res.json();
    // L'API enveloppe les matchs dans { games: [...] }
    const games: ApiGame[] = json.games ?? (Array.isArray(json) ? json : []);
    if (!games.length) return null;

    const matches: WC26Match[] = [];
    const results: WC26Result[] = [];

    for (const g of games) {
      const hasTeams = !!g.home_team_name_en;
      const t1 = g.home_team_name_en ?? g.home_team_label ?? `TBD (${g.home_team_id})`;
      const t2 = g.away_team_name_en ?? g.away_team_label ?? `TBD (${g.away_team_id})`;
      const { date, koUTC } = parseLocalDate(g.local_date, g.stadium_id);

      const status: WC26Match["status"] =
        g.finished === "TRUE"
          ? "finished"
          : g.time_elapsed !== "notstarted"
          ? "live"
          : "scheduled";

      matches.push({
        id: `wc26_${g.id}`,
        grp: g.group,
        date,
        koUTC,
        t1,
        t2,
        // Cotes par défaut — écrasées par The Odds API si disponibles
        p1: 0.37,
        pX: 0.26,
        p2: 0.37,
        status,
        label1: hasTeams ? undefined : g.home_team_label,
        label2: hasTeams ? undefined : g.away_team_label,
      });

      // Injecte le résultat pour TOUT match terminé (y compris 0-0)
      if (g.finished === "TRUE") {
        results.push({
          matchId: `wc26_${g.id}`,
          o: toOutcome(g.home_score, g.away_score),
          s: `${g.home_score}-${g.away_score}`,
        });
      }
    }

    cachedData = { matches, results };
    cacheTime = Date.now();
    return cachedData;
  } catch {
    // CORS, réseau ou parsing — fallback silencieux vers Supabase
    return null;
  }
}

/** Invalide le cache (ex: refresh manuel) */
export function invalidateWC26Cache() {
  cachedData = null;
  cacheTime = 0;
}
